import React from "react";
import { Alert, message, Tooltip, Button, Space } from "antd";
import { useQuery, useMutation } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { DeleteFilled } from "@ant-design/icons";

// graphql
import GET_PAYMENT_METHODS from "graphql/queries/GetPaymentMethods";
import REMOVE_PAYMENT_METHOD from "graphql/mutations/RemovePaymentMethod";
import SET_PM_AS_DEFAULT from "graphql/mutations/SetPmAsDefault";

// styling

// types
import { PaymentMethod } from "types";

import useErrors from "hooks/useErrors";
import useModal from "hooks/useModal";
import ErrorAlerting from "common/error_alerting";
import DeletePaymentMethodModal from "common/modal/delete_payment_method_modal";
import {
  CustomRow,
  PaymentMethodContainer,
  CardNumber,
  DefaultTag,
} from "./MyBillingsStyling";

const PaymentMethodList = () => {
  const { t } = useTranslation("my_billing");
  const { errors, handleError, closeError } = useErrors();
  const [deletingPaymentModalVisible, showModal, closeModal] = useModal();
  // graphql hooks
  const { data, error, loading } = useQuery(GET_PAYMENT_METHODS, {
    fetchPolicy: "network-only",
  });
  const [removePaymentMethod, { loading: removePaymentMethodLoading }] =
    useMutation(REMOVE_PAYMENT_METHOD, {
      refetchQueries: [
        {
          query: GET_PAYMENT_METHODS,
        },
      ],
    });
  const [setPmAsDefault] = useMutation(SET_PM_AS_DEFAULT, {
    refetchQueries: [
      {
        query: GET_PAYMENT_METHODS,
      },
    ],
  });

  if (loading) {
    return <Alert type="info" message={t("Loading payment methods...")} />;
  }

  if (error) {
    return (
      <Alert
        type="error"
        message={t(
          "Error loading payment methods. Please check your internet or let us know if this keeps happening."
        )}
      />
    );
  }

  const {
    me: { paymentMethods },
  } = data;

  const handleRemovePaymentMethod = async (paymentMethodId: string) => {
    try {
      await removePaymentMethod({
        variables: {
          paymentMethodId,
        },
      });

      message.success(t("Payment method removed successfully!"));
    } catch (e) {
      handleError(e);
    }
  };

  const handleSetPmAsDefault = async (
    paymentMethod: PaymentMethod | undefined
  ) => {
    if (paymentMethod) {
      try {
        await setPmAsDefault({
          variables: {
            input: {
              attributes: {
                paymentMethodId: paymentMethod.id,
              },
            },
          },
        });

        message.success(t("Default payment method set successfully!"));
      } catch (e) {
        handleError(e);
      }
    }
  };

  const renderDefaultTag = (isDefault?: boolean) => {
    if (isDefault) {
      return <DefaultTag>{t("DEFAULT")}</DefaultTag>;
    }
    return null;
  };

  const renderHelpText = () => {
    if (paymentMethods && paymentMethods.length > 0) {
      return (
        <span style={{ color: "#707070" }}>
          {t(
            "This is your default payment method. It will never be charged without an active subscription. 100% guaranteed."
          )}
        </span>
      );
    }
    return (
      <span style={{ color: "#282828" }}>
        {t(
          "You don't have any payment method yet. Add a new one to buy more credits."
        )}
      </span>
    );
  };

  return (
    <div>
      <ErrorAlerting errors={errors} onClose={closeError} />
      {paymentMethods.map((paymentMethod: PaymentMethod) => (
        <CustomRow key={paymentMethod.id}>
          <PaymentMethodContainer>
            {paymentMethod.brand} :
            <CardNumber>**** **** **** {paymentMethod.last4}</CardNumber>
            {renderDefaultTag(paymentMethod.default)}
          </PaymentMethodContainer>
          <Space>
            {!paymentMethod.default && (
              <Button
                type="default"
                size="small"
                onClick={() => handleSetPmAsDefault(paymentMethod)}
              >
                {t("Set as default")}
              </Button>
            )}
            <Tooltip title={t("Remove")}>
              <Button
                icon={<DeleteFilled />}
                loading={removePaymentMethodLoading}
                onClick={() => showModal()}
              />
            </Tooltip>
            <DeletePaymentMethodModal
              visible={deletingPaymentModalVisible}
              onCloseModal={() => closeModal()}
              onRemovePaymentMethod={() =>
                handleRemovePaymentMethod(paymentMethod.id)
              }
            />
          </Space>
        </CustomRow>
      ))}
      {renderHelpText()}
    </div>
  );
};

export default PaymentMethodList;
